import { ReactNode, createContext, useEffect, useReducer } from 'react'
import axios from 'axios'

interface User {
  email: string
  permission: string
  // Add other properties as needed
}

interface AuthState {
  accessToken: string | null
  user: User | null
}

type AuthAction =
  | { type: 'SET_ACCESS_TOKEN'; payload: string }
  | { type: 'SET_USER'; payload: User }

// Define the context type
export interface AuthContextProps {
  state: AuthState
  setAccessToken: (accessToken: string | null) => void
  // setUser: (user: User | null) => void;
  dispatch: React.Dispatch<AuthAction>
}

// Create the context
export const AuthContext = createContext<AuthContextProps | undefined>(
  undefined
)

// Define initial state
const initialState: AuthState = {
  accessToken: null,
  user: null,
}

// Create the reducer function
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_ACCESS_TOKEN':
      return { ...state, accessToken: action.payload }
    case 'SET_USER':
      return { ...state, user: action.payload }
    default:
      return state
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  const setAccessToken = (accessToken: string | null) => {
    if (accessToken !== null) {
      dispatch({ type: 'SET_ACCESS_TOKEN', payload: accessToken })
    }
  }

  const fetchData = (token: string) => {
    return axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/auth/decode`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data)
      .catch((error) => {
        console.error('에러 발생:', error)
        throw error // 에러를 상위로 전파
      })
  }

  useEffect(() => {
    state.accessToken &&
      fetchData(state.accessToken)
        .then((res) => {
          dispatch({
            type: 'SET_USER',
            payload: { email: res.user.email, permission: res.user.permission },
          })
          // 여기에서 데이터를 사용할 수 있음
        })
        .catch((error) => {
          console.error('에러 발생:', error)
        })
  }, [state.accessToken])

  const contextValue: AuthContextProps = {
    state,
    setAccessToken,
    dispatch,
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}
